import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { generateClient } from 'aws-amplify/data';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SQSClient, SendMessageCommand } from '@aws-sdk/client-sqs';

import awsConfig from '../../../amplify_outputs.json';
import { normalizeName, normalizeRegId } from '../../utils/normalizeCompanyName';

// ISO 3166-1 alpha-2 country codes validation
const ISO_COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
];

const isValidISOCountryCode = (code) => ISO_COUNTRY_CODES.includes(code?.toUpperCase());

const normalizeLanguage = (language) => {
  if (!language) return 'EN';
  
  const lang = language.toString().toLowerCase().trim();
  
  // Direct matches for EN/HE
  if (lang === 'en' || lang === 'he') {
    return lang.toUpperCase();
  }
  
  // Full word matches
  if (lang === 'english' || lang.includes('english')) {
    return 'EN';
  }
  
  if (lang === 'hebrew' || lang.includes('hebrew') || lang === 'עברית') {
    return 'HE';
  }
  
  // Default to EN if unrecognized
  console.warn(`[BULK UPLOAD] Unrecognized language "${language}", defaulting to EN`);
  return 'EN';
};

const isValidEmail = (email) => {
  if (!email || email.trim() === '') return true; // Empty email is allowed
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const isValidWebsite = (website) => {
  if (!website || website.trim() === '') return true; // Empty website is allowed
  const cleanWebsite = website.trim();

  try {
    // Try as-is (handles URLs with protocol)
    const _url = new URL(cleanWebsite);
    return true;
  } catch {
    try {
      // Try adding http:// if protocol is missing
      const _url = new URL(`http://${cleanWebsite}`);
      return true;
    } catch {
      return false;
    }
  }
};

// Helper function to get cell value by column letter and row number
const getCellValue = (worksheet, column, row) => {
  const cellAddress = `${column}${row}`;
  const cell = worksheet[cellAddress];
  return cell ? cell.v : undefined;
};

/**
 * @type {import('aws-amplify/data').Client<import('../../../amplify/data/resource').Schema>}
 */
const client = generateClient();

const sqsClient = new SQSClient({
  region: 'eu-west-1',
});

export const createReport = createAsyncThunk(
  'reports/createReport',
  async (
    {
      companyName,
      nativeCompanyName,
      incorporation,
      registrationNumber,
      website,
      city,
      street,
      individualName,
      nativeIndividualName,
      idNumber,
      birthDate,
      nationality,
      address,
      title,
      originCountry,
      country,
      email,
      phone,
      type,
      userId,
      language,
      financial,
      cyber,
      owner,
      reviewer,
      userInput,
      groupId,
    },
    { rejectWithValue }
  ) => {
    try {
      let reportPayload = {};

      const searchFields = (() => {
        try {
          if (type === 'COMPANY' || type === 'SCREENING') {
            return {
              searchName: normalizeName(companyName) || undefined,
              searchNativeName: normalizeName(nativeCompanyName) || undefined,
              searchRegId: normalizeRegId(registrationNumber) || undefined,
            };
          }
          if (type === 'INDIVIDUAL') {
            return {
              searchName: normalizeName(individualName) || undefined,
              searchNativeName: normalizeName(nativeIndividualName) || undefined,
              searchRegId: normalizeRegId(idNumber) || undefined,
            };
          }
        } catch (err) {
          console.error('[reports.slice] failed to derive search fields, continuing', err);
        }
        return {};
      })();

      if (type === 'COMPANY') {
        reportPayload = {
          userId,
          reviewerId: reviewer,
          ownerId: owner,
          groupId: groupId || undefined,
          type: 'COMPANY',
          status: 'IN_PROGRESS',
          language,
          ...searchFields,
          company: {
            companyName,
            nativeCompanyName,
            incorporation,
            registrationNumber,
            website,
            city,
            street,
            financial,
            cyber,
          },
        };
      } else if (type === 'INDIVIDUAL') {
        reportPayload = {
          userId,
          reviewerId: reviewer,
          ownerId: owner,
          groupId: groupId || undefined,
          type: 'INDIVIDUAL',
          status: 'IN_PROGRESS',
          language,
          ...searchFields,
          individual: {
            individualName,
            nativeIndividualName,
            country,
            nationality,
            title,
            originCountry,
            address,
            idNumber,
            company: companyName,
            email,
            phone,
          },
        };
      } else if (type === 'SCREENING') {
        reportPayload = {
          userId,
          reviewerId: reviewer,
          ownerId: owner,
          groupId: groupId || undefined,
          type: 'SCREENING',
          status: 'IN_PROGRESS',
          language,
          ...searchFields,
          screening: {
            companyName,
            nativeCompanyName,
            incorporation,
            registrationNumber,
            website,
            city,
            street,
          },
        };
      } else {
        throw new Error('Invalid report type');
      }

      const response = await client.models.Report.create(reportPayload);

      const reportId = response.data.id;

      const userInputDataForCreate = {
        ...userInput,
        reportId,
      };

      await client.models.UserInput.create(userInputDataForCreate);

      await client.models.Chat.create({
        reportId,
      });

      const authToken = awsConfig.data.api_key;

      const arns = {
        COMPANY:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('companyStepFunctionArn'))
          ],
        INDIVIDUAL:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('individualStepFunctionArn'))
          ],
        SCREENING:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('screeningStepFunctionArn'))
          ],
      };

      const executionInput = {
        reportId,
        userId,
        authToken,
        groupId: groupId || undefined,
      };

      const sqsMessage = {
        type,
        arns,
        payload: executionInput,
      };

      const triggerQueueResponse = await client.queries.triggerQueue({
        type,
        arns,
        reportId,
        userId,
        authToken,
        groupId: groupId || undefined,
        queueUrl: awsConfig.custom.reportsQueueUrl,
      });

      return triggerQueueResponse.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(
        error.message || 'An error occurred during report creation or Step Function execution.'
      );
    }
  }
);

export const followUpReport = createAsyncThunk(
  'reports/followUpReport',
  async (
    { reportId, userId, type, groupId, notReduceFromQuota = false },
    { rejectWithValue }
  ) => {
    try {
      const authToken = awsConfig.data.api_key;

      await client.models.Report.update({
        id: reportId,
        status: 'IN_PROGRESS',
        notReduceFromQuota,
      });

      const arns = {
        COMPANY:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('companyStepFunctionArn'))
          ],
        INDIVIDUAL:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('individualStepFunctionArn'))
          ],
        SCREENING:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('screeningStepFunctionArn'))
          ],
      };

      const executionInput = {
        reportId,
        userId,
        authToken,
        groupId: groupId || undefined,
      };

      const sqsMessage = {
        type,
        arns,
        payload: executionInput,
      };

      const triggerQueueResponse = await client.queries.triggerQueue({
        type,
        arns,
        reportId,
        userId,
        authToken,
        groupId: groupId || undefined,
        queueUrl: awsConfig.custom.reportsQueueUrl,
      });

      return triggerQueueResponse.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(
        error.message || 'An error occurred during report creation or Step Function execution.'
      );
    }
  }
);

export const updateReport = createAsyncThunk(
  'reports/updateReport',
  async (
    {
      reportId,
      companyName,
      incorporation,
      website,
      city,
      street,
      individualName,
      type,
      userId,
      language,
      financial,
      cyber,
    },
    { rejectWithValue }
  ) => {
    try {
      console.log(`[START] Updating report with ID: ${reportId}`);
      console.log(`[INFO] Parameters received:`, {
        reportId,
        companyName,
        incorporation,
        website,
        city,
        street,
        individualName,
        type,
        userId,
        language,
        financial,
        cyber,
      });

      let reportPayload = {};
      console.log(`[INFO] Determining report payload for type: ${type}`);

      if (type === 'COMPANY') {
        console.log(`[INFO] Creating payload for COMPANY`);
        reportPayload = {
          id: reportId,
          type: 'COMPANY',
          status: 'IN_PROGRESS',
          manualInput: true,
          language,
          company: {
            companyName,
            incorporation,
            financial,
            cyber,
          },
        };
      } else if (type === 'INDIVIDUAL') {
        console.log(`[INFO] Creating payload for INDIVIDUAL`);
        reportPayload = {
          id: reportId,
          type: 'INDIVIDUAL',
          status: 'IN_PROGRESS',
          language,
          individual: {
            individualName,
          },
        };
      } else if (type === 'SCREENING') {
        console.log(`[INFO] Creating payload for SCREENING`);
        reportPayload = {
          id: reportId,
          type: 'SCREENING',
          status: 'IN_PROGRESS',
          manualInput: true,
          language,
          screening: {
            companyName,
            incorporation,
            website,
            city,
            street,
          },
        };
      } else {
        console.error(`[ERROR] Invalid report type provided: ${type}`);
        throw new Error('Invalid report type');
      }

      console.log(`[INFO] Payload created successfully: ${JSON.stringify(reportPayload, null, 2)}`);

      console.log(`[INFO] Sending payload to update the report in database`);
      const response = await client.models.Report.update(reportPayload);

      console.log(`[SUCCESS] Report updated successfully in database.`);
      console.log(`[INFO] Database response:`, response);

      console.log(`[INFO] Retrieving auth token`);
      const authToken = awsConfig.data.api_key;
      console.log(`[INFO] Auth token retrieved successfully`);

      console.log(`[INFO] Fetching ARNs for Step Function execution`);
      const arns = {
        COMPANY:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('companyStepFunctionArn'))
          ],
        INDIVIDUAL:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('individualStepFunctionArn'))
          ],
        SCREENING:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('screeningStepFunctionArn'))
          ],
      };
      console.log(`[INFO] ARNs retrieved successfully: ${JSON.stringify(arns, null, 2)}`);

      console.log(`[INFO] Preparing execution input for Step Function`);
      const executionInput = {
        reportId,
        userId,
        authToken,
      };
      console.log(`[INFO] Execution input prepared: ${JSON.stringify(executionInput, null, 2)}`);

      console.log(`[INFO] Preparing SQS message`);
      const sqsMessage = {
        type,
        arns,
        payload: executionInput,
      };
      console.log(`[INFO] SQS message prepared: ${JSON.stringify(sqsMessage, null, 2)}`);

      console.log(`[INFO] Sending SQS message to queue: ${awsConfig.custom.reportsQueueUrl}`);
      const sendMessageCommand = new SendMessageCommand({
        QueueUrl: awsConfig.custom.reportsQueueUrl,
        MessageBody: JSON.stringify(sqsMessage),
        MessageGroupId: `Group-${Math.random().toString(36).substring(2, 15)}`,
      });

      const sqsResponse = await sqsClient.send(sendMessageCommand);
      console.log(`[SUCCESS] Message sent to SQS successfully.`);
      console.log(`[INFO] SQS response:`, sqsResponse);

      console.log(`[END] Report update process completed successfully`);
      return response.data;
    } catch (error) {
      console.error(`[ERROR] An error occurred during the report update process.`);
      console.error(`[ERROR] Full error details:`, error);

      return rejectWithValue(
        error.message || 'An error occurred during report creation or Step Function execution.'
      );
    }
  }
);

export const bulkReports = createAsyncThunk(
  'reports/bulkReports',
  async ({ type, userId, owner, reviewer, file, skipIndices = [] }, { rejectWithValue }) => {
    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet);

      if (rows.length === 0) {
        throw new Error('The file is empty. Please provide at least one filled row.');
      }

      // Filter rows based on key field presence to skip empty rows
      // For INDIVIDUAL: only rows with 'English Full Name*'
      // For COMPANY/SCREENING: only rows with 'Company Name*'
      const filteredRows = rows.filter((row, index) => {
        if (type === 'INDIVIDUAL') {
          return row['English Full Name*'] && row['English Full Name*'].toString().trim() !== '';
        }
        // COMPANY and SCREENING
        return row['Company Name*'] && row['Company Name*'].toString().trim() !== '';
      });

      if (filteredRows.length === 0) {
        throw new Error('No valid rows found. Please ensure at least one row has the required key field filled.');
      }

      const validationErrors = [];

      filteredRows.forEach((row, index) => {
        // Find the original index to get correct Excel row number
        const originalIndex = rows.indexOf(row);
        const rowNumber = originalIndex + 2; // Excel row number (starts from row 2)

        // Get country values from Value columns (dropdown data validation)
        const incorporationValue = getCellValue(worksheet, 'Y', rowNumber); // Value 1 for COMPANY/SCREENING
        const countryOfStayValue = getCellValue(worksheet, 'Z', rowNumber); // Value 3 for INDIVIDUAL

        // Validate email and website for all types
        if (row.Email && !isValidEmail(row.Email)) {
          validationErrors.push(`Row ${rowNumber}: Invalid email format "${row.Email}"`);
        }

        if (row.Website && !isValidWebsite(row.Website)) {
          validationErrors.push(`Row ${rowNumber}: Invalid website format "${row.Website}"`);
        }

        // Type-specific validations
        if (type === 'COMPANY') {
          const hasRequiredFields = row['Company Name*'] && incorporationValue;
          const hasOptionalFields = row.Email || row.Website || row.Address;
          if (!hasRequiredFields) {
            validationErrors.push(
              `Row ${rowNumber}: "Company Name" and "Incorporation" are required fields.`
            );
          }
          if (incorporationValue && !isValidISOCountryCode(incorporationValue)) {
            validationErrors.push(
              `Row ${rowNumber}: "Incorporation" must be a valid ISO 3166-1 alpha-2 country code (e.g., "US", "GB", "DE").`
            );
          }
          if (!hasOptionalFields) {
            validationErrors.push(
              `Row ${rowNumber}: At least one of "Email", "Website", or "Address" must be filled.`
            );
          }
        }

        if (type === 'SCREENING') {
          const hasRequiredFields = row['Company Name*'] && incorporationValue;
          const hasOptionalFields = row.Email || row.Website || row.Address;
          if (!hasRequiredFields) {
            validationErrors.push(
              `Row ${rowNumber}: "Company Name" and "Incorporation" are required fields.`
            );
          }
          if (incorporationValue && !isValidISOCountryCode(incorporationValue)) {
            validationErrors.push(
              `Row ${rowNumber}: "Incorporation" must be a valid ISO 3166-1 alpha-2 country code (e.g., "US", "GB", "DE").`
            );
          }
          if (!hasOptionalFields) {
            validationErrors.push(
              `Row ${rowNumber}: At least one of "Email", "Website", or "Address" must be filled.`
            );
          }
        }

        if (type === 'INDIVIDUAL') {
          const hasRequiredFields = row['English Full Name*'] && countryOfStayValue;
          if (!hasRequiredFields) {
            validationErrors.push(
              `Row ${rowNumber}: "Individual Name" and "Country of Stay" are required fields.`
            );
          }
        }
      });

      // If there are any validation errors, stop processing and return all errors
      if (validationErrors.length > 0) {
        const errorMessage = `Validation failed:\n${validationErrors.join('\n')}`;
        console.error('[BULK UPLOAD] Validation errors found:', validationErrors);
        throw new Error(errorMessage);
      }

      const skipSet = new Set(skipIndices);
      const rowsForCreation = filteredRows.filter((_, idx) => !skipSet.has(idx));
      if (rowsForCreation.length === 0) {
        console.log('[BULK UPLOAD] All rows skipped via duplicate review');
        return { reportIds: [] };
      }

      const reports = rowsForCreation.map((row, index) => {
        const normalizedLanguage = normalizeLanguage(row['Language*']);
        console.log(`[BULK UPLOAD] Row ${index + 1} - Original language: "${row['Language*']}" -> Normalized: "${normalizedLanguage}"`);

        const baseReport = {
          userId,
          ownerId: owner,
          type,
          status: 'IN_PROGRESS',
          language: normalizedLanguage,
        };

        // Only add reviewerId if it's defined and not null
        if (reviewer && reviewer !== 'undefined') {
          baseReport.reviewerId = reviewer;
        }

        // Excel row number (data starts from row 2, row 1 is headers)
        // Find original index in rows array to get correct Excel row
        const originalIndex = rows.indexOf(row);
        const excelRow = originalIndex + 2;

        const deriveSearch = (name, regId) => {
          try {
            return {
              searchName: normalizeName(name) || undefined,
              searchRegId: normalizeRegId(regId) || undefined,
            };
          } catch (err) {
            console.error('[BULK UPLOAD] failed to derive search fields, continuing', err);
            return {};
          }
        };

        if (type === 'COMPANY') {
          return {
            ...baseReport,
            ...deriveSearch(row['Company Name*'], undefined),
            company: {
              companyName: row['Company Name*'],
              incorporation: getCellValue(worksheet, 'Y', excelRow), // Value 1
              email: row.Email,
              website: row.Website,
              address: row.Address,
              financial: row['Financial Add-on'],
              cyber: row['Cyber Add-on'],
            },
          };
        }

        if (type === 'SCREENING') {
          return {
            ...baseReport,
            ...deriveSearch(row['Company Name*'], undefined),
            screening: {
              companyName: row['Company Name*'],
              incorporation: getCellValue(worksheet, 'Y', excelRow), // Value 1
              email: row.Email,
              website: row.Website,
              address: row.Address,
            },
          };
        }

        if (type === 'INDIVIDUAL') {
          return {
            ...baseReport,
            ...deriveSearch(row['English Full Name*'], undefined),
            individual: {
              individualName: row['English Full Name*'],
              country: getCellValue(worksheet, 'Z', excelRow), // Value 3 - Country of Stay
              nationality: getCellValue(worksheet, 'X', excelRow), // Value 1
              title: row['Job Title'],
              originCountry: getCellValue(worksheet, 'Y', excelRow), // Value 2 - Place of Birth
              address: row['Full Address'],
              company: row['Company Name'],
              email: row.Email,
              phone: row['Phone Number'],
            },
          };
        }

        throw new Error('Invalid report type');
      });

      const createdReports = await Promise.all(
        reports.map(async (reportPayload, index) => {
          try {
            console.log(`[BULK UPLOAD] Creating report ${index + 1}/${reports.length}:`, reportPayload);
            const response = await client.models.Report.create(reportPayload);
            console.log(`[BULK UPLOAD] Report ${index + 1} response:`, response);
            
            if (!response) {
              console.error(`[BULK UPLOAD] Report ${index + 1} - Response is null/undefined:`, response);
              throw new Error('Failed to create report - invalid response from database');
            }
            if (!response.data) {
              console.error(`[BULK UPLOAD] Report ${index + 1} - Response.data is null/undefined:`, response);
              if (response.errors && response.errors.length > 0) {
                console.error(`[BULK UPLOAD] Report ${index + 1} - Database errors:`, response.errors);
                const errorMessage = response.errors.map(err => err.message || JSON.stringify(err)).join(', ');
                throw new Error(`Database validation error: ${errorMessage}`);
              }
              throw new Error('Failed to create report - invalid response from database');
            }
            if (!response.data.id) {
              console.error(`[BULK UPLOAD] Report ${index + 1} - Response.data.id is null/undefined:`, response.data);
              throw new Error('Failed to create report - missing report ID');
            }
            
            console.log(`[BULK UPLOAD] Report ${index + 1} created successfully with ID:`, response.data.id);
            return response;
          } catch (error) {
            console.error(`[BULK UPLOAD] Failed to create report ${index + 1}:`, error);
            console.error(`[BULK UPLOAD] Report payload that failed:`, reportPayload);
            throw new Error(`Failed to create report: ${error.message}`);
          }
        })
      );

      console.log('Reports Created:', createdReports);

      const reportIds = createdReports.map((response, index) => {
        if (!response?.data?.id) {
          console.error(`[BULK UPLOAD] Report ${index + 1} - Missing ID in response:`, response);
          throw new Error('Report creation failed - missing report ID');
        }
        return response.data.id;
      });

      const authToken = awsConfig.data.api_key;

      const arns = {
        COMPANY:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('companyStepFunctionArn'))
          ],
        INDIVIDUAL:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('individualStepFunctionArn'))
          ],
        SCREENING:
          awsConfig.custom[
            Object.keys(awsConfig.custom).find((key) => key.startsWith('screeningStepFunctionArn'))
          ],
      };

      const triggerQueueInputs = reportIds.map((reportId) => ({
        type,
        arns,
        reportId,
        userId,
        authToken,
        queueUrl: awsConfig.custom.reportsQueueUrl,
      }));

      const triggerQueueResponses = await Promise.all(
        triggerQueueInputs.map((input) => client.queries.triggerQueue(input))
      );

      console.log('Messages sent to queue:', triggerQueueResponses);

      return createdReports.map((response) => response.data);
    } catch (error) {
      console.error('[BULK UPLOAD] Error during report creation or queue execution:', error);
      console.error('[BULK UPLOAD] Error stack:', error.stack);
      console.error('[BULK UPLOAD] Error name:', error.name);
      console.error('[BULK UPLOAD] Error message:', error.message);
      console.error('[BULK UPLOAD] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      let userFriendlyMessage = 'An error occurred while processing your bulk upload.';
      
      if (error.message.includes('Validation failed:')) {
        userFriendlyMessage = error.message; // Show the detailed validation errors
      } else if (error.message.includes('Database validation error')) {
        userFriendlyMessage = 'Database validation failed. Please check your file format and ensure all required fields are provided correctly.';
      } else if (error.message.includes('Failed to create report')) {
        userFriendlyMessage = 'Failed to create one or more reports. Please check your file format and try again.';
      } else if (error.message.includes('Validation error')) {
        userFriendlyMessage = error.message;
      } else if (error.message.includes('empty')) {
        userFriendlyMessage = 'The uploaded file is empty. Please provide a file with data.';
      } else if (error.message.includes('database')) {
        userFriendlyMessage = 'Database connection error. Please try again later.';
      } else if (error.message.includes('queue')) {
        userFriendlyMessage = 'Reports were created but failed to start processing. Please contact support.';
      }
      
      console.error('[BULK UPLOAD] User will see this message:', userFriendlyMessage);
      return rejectWithValue(userFriendlyMessage);
    }
  }
);

export const passControl = createAsyncThunk(
  'reports/passControl',
  async ({ reportId, analystId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Report.update({
        id: reportId,
        reviewerId: analystId,
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const updateScreeningReport = createAsyncThunk(
  'reports/updateScreeningReport',
  async ({ reportId, path, value }, { getState, rejectWithValue }) => {
    try {
      const currentReport = getState().reports.report;

      if (!currentReport) {
        throw new Error('Report not found in state');
      }

      const updatedReport = JSON.parse(JSON.stringify(currentReport));
      let target = updatedReport.screening;

      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        const match = key.match(/(\w+)\[(\d+)\]/);
        if (match) {
          target = target[match[1]][parseInt(match[2], 10)];
        } else {
          target = target[key];
        }
      }

      target[keys[keys.length - 1]] = value;

      const response = await client.models.Report.update({
        id: reportId,
        screening: updatedReport.screening,
      });

      await client.models.ChangeLog.create({
        reportId,
        type: 'UPDATE',
        content: JSON.stringify({
          path,
          value,
        }),
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'An error occurred while updating the report.');
    }
  }
);

export const updateCompanyReport = createAsyncThunk(
  'reports/updateCompanyReport',
  async (
    { reportId, path, findingsId, value, operation = 'update' },
    { getState, rejectWithValue }
  ) => {
    try {
      console.log('Starting updateCompanyReport with arguments:', {
        reportId,
        path,
        findingsId,
        value,
        operation,
      });

      console.log(reportId);

      const currentReport = getState().reports.report;

      if (!currentReport) {
        throw new Error('Report not found in state');
      }

      console.log('Current report state:', JSON.stringify(currentReport, null, 2));

      const updatedReport = JSON.parse(JSON.stringify(currentReport));
      let target = updatedReport.company;

      const keys = path.split('.');
      console.log('Parsed path keys:', keys);

      if (path.startsWith('findings[')) {
        const findingsArrayKey = keys[0].split('[')[0];
        target = target[findingsArrayKey];

        const index = target.findIndex((item) => item.id === findingsId);
        if (index === -1) {
          throw new Error(`Finding with id ${findingsId} not found`);
        }

        console.log(`Targeting finding with id ${findingsId} at index ${index}`);

        if (operation === 'delete') {
          console.log(`Deleting finding with id ${findingsId}`);
          target.splice(index, 1);
        } else {
          console.log(`Updating finding with id ${findingsId} with value:`, value);
          target[index] = { ...target[index], ...value };
        }
      } else {
        for (let i = 0; i < keys.length - 1; i += 1) {
          const key = keys[i];
          target = target[key];
        }

        const lastKey = keys[keys.length - 1];
        target[lastKey] = operation === 'update' ? value : undefined;
      }

      if (updatedReport.company.findings) {
        updatedReport.company.findings = updatedReport.company.findings.map(
          ({ id, ...rest }) => rest
        );
      }

      console.log('Updated report state before API call:', JSON.stringify(updatedReport, null, 2));

      const response = await client.models.Report.update({
        id: reportId,
        company: updatedReport.company,
      });

      console.log('API response:', response.data);

      await client.models.ChangeLog.create({
        reportId,
        type: operation.toUpperCase(),
        content: JSON.stringify({
          path,
          findingsId,
          value,
        }),
      });

      console.log('Change log successfully created');

      return response.data;
    } catch (error) {
      console.error('Error in updateCompanyReport:', error);
      return rejectWithValue(error.message || 'An error occurred while updating the report.');
    }
  }
);

export const approveReport = createAsyncThunk(
  'reports/approveReport',
  async ({ reportId, ownerId, title }, { rejectWithValue }) => {
    try {
      const response = await client.models.Report.update({
        id: reportId,
        status: 'COMPLETED',
      });

      await client.models.Notification.create({
        reportId,
        userId: ownerId,
        type: 'REPORT',
        isUnread: true,
        title: `Report ${title} has been approved`,
        category: 'Report',
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchReport = createAsyncThunk(
  'reports/fetchReport',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await client.models.Report.get({
        id,
        selectionSet: [
          'id',
          'status',
          'type',
          'approved',
          'language',
          'error',
          'createdAt',
          'updatedAt',
          'company.*',
          'company.motherOwnership.*',
          'company.infoMotherOwnership.*',
          'company.ownerSanctions.*',
          'individual.*',
          'screening.*',
          'screening.motherOwnership.*',
          'screening.infoMotherOwnership.*',
          'ownerId',
          'owner.fullName',
          'reviewerId',
          'reviewer.*',
          'user.*',
        ],
      });

      const userResponse = await client.models.User.get({
        id: response.data.ownerId,
        selectionSet: ['fullName', 'email'],
      });

      response.data.owner = userResponse.data;

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchReports = createAsyncThunk(
  'reports/fetchReports',
  async ({ nextToken = null, limit = 10, userId, isAnalyst }, { rejectWithValue }) => {
    try {
      let response;

      if (userId && isAnalyst) {
        response = await client.models.Report.listReportByReviewerId({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          nextToken,
          limit,
          reviewerId: userId,
          selectionSet: [
            'id',
            'status',
            'type',
            'approved',
            'language',
            'error',
            'createdAt',
            'updatedAt',
            'company.*',
            'individual.*',
            'screening.*',
            'ownerId',
            'owner.*',
            'reviewerId',
            'reviewer.*',
            'user.*',
          ],
        });
      } else if (userId) {
        response = await client.models.Report.listReportByOwnerId({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          nextToken,
          limit,
          ownerId: userId,
          selectionSet: [
            'id',
            'status',
            'type',
            'approved',
            'language',
            'error',
            'createdAt',
            'updatedAt',
            'company.*',
            'individual.*',
            'screening.*',
            'ownerId',
            'owner.*',
            'reviewerId',
            'reviewer.*',
            'user.*',
          ],
        });
      } else {
        response = await client.models.Report.list({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          nextToken,
          limit,
          selectionSet: [
            'id',
            'status',
            'type',
            'approved',
            'language',
            'error',
            'createdAt',
            'updatedAt',
            'company.*',
            'individual.*',
            'screening.*',
            'ownerId',
            'owner.*',
            'reviewerId',
            'reviewer.*',
            'user.*',
          ],
        });
      }

      return {
        data: response.data,
        nextToken: response.nextToken || null,
      };
    } catch (error) {
      console.error(error);
      return rejectWithValue(error.message || 'An error occurred while fetching reports.');
    }
  }
);

export const fetchReportsLength = createAsyncThunk(
  'reports/fetchReportsLength',
  async ({ userId, isAnalyst }, { rejectWithValue }) => {
    try {
      let response;

      if (userId && isAnalyst) {
        response = await client.models.Report.listReportByReviewerId({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          reviewerId: userId,
          selectionSet: ['id'],
        });
      } else if (userId) {
        response = await client.models.Report.listReportByOwnerId({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          ownerId: userId,
          selectionSet: ['id'],
        });
      } else {
        response = await client.models.Report.list({
          authMode: 'apiKey',
          authToken: awsConfig.data.api_key,
          selectionSet: ['id'],
        });
      }

      if (!response) {
        console.error('[ERROR] API response is undefined or null');
        return 0;
      }

      if (!response.data) {
        console.error('[ERROR] response.data is undefined or null');
        return 0;
      }

      const reports = Array.isArray(response.data) ? response.data : [];

      if (!Array.isArray(reports)) {
        console.warn('[WARN] response.data.items is not an array');
      } else {
        console.log(`[INFO] Successfully extracted reports. Count: ${reports.length}`);
      }

      return reports.length;
    } catch (error) {
      console.error('[ERROR] An error occurred while fetching reports:', error);
      return rejectWithValue(error.message || 'An error occurred while fetching reports.');
    }
  }
);

export const fetchTopCustomers = createAsyncThunk(
  'reports/fetchTopCustomers',
  async ({ time }, { rejectWithValue }) => {
    try {
      const response = await client.queries.fetchTopCustomers({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        time,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      console.log(body.topCustomers);

      return body.topCustomers;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchTotalReports = createAsyncThunk(
  'reports/fetchTotalReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.countTotalReports({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      return body.totalReports;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchAnalystsReports = createAsyncThunk(
  'reports/fetchAnalystsReport',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.countAnalystsReports({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      return body.totalReports;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchCustomersReports = createAsyncThunk(
  'reports/fetchCustomersReport',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.countCustomersReports({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      return body.totalReports;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchStatusReports = createAsyncThunk(
  'reports/fetchStatusReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.countStatusReports({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      return body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchReportsOverview = createAsyncThunk(
  'reports/fetchReportsOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.fetchReportsOverview({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      console.log(body);

      return body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  reports: [],
  report: null,

  nextToken: null,

  reportsLength: 0,

  totalReports: 0,
  analystsReports: 0,
  customersReports: 0,

  statusReports: {
    completed: 0,
    inProgress: 0,
    pendingReview: 0,
    failed: 0,
  },

  topCustomers: [],

  weeklyReports: {
    data: {
      analysts: [0, 0, 0, 0],
      customers: [0, 0, 0, 0],
    },
    categories: ['W1', 'W2', 'W3', 'W4'],
  },

  monthlyReports: {
    data: {
      analysts: Array(12).fill(0),
      customers: Array(12).fill(0),
    },
    categories: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  },
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportsOverview.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReportsOverview.fulfilled, (state, action) => {
        state.loading = false;

        const weeklySeries = action.payload.series.find((s) => s.name === 'Weekly');
        const monthlySeries = action.payload.series.find((s) => s.name === 'Monthly');

        if (weeklySeries) {
          const analystsData = weeklySeries.data.find((item) => item.name === 'By Analysts');
          const customersData = weeklySeries.data.find((item) => item.name === 'By Customers');

          state.weeklyReports.data.analysts = analystsData ? analystsData.data : [0, 0, 0, 0];
          state.weeklyReports.data.customers = customersData ? customersData.data : [0, 0, 0, 0];
        }

        if (monthlySeries) {
          const analystsData = monthlySeries.data.find((item) => item.name === 'By Analysts');
          const customersData = monthlySeries.data.find((item) => item.name === 'By Customers');

          state.monthlyReports.data.analysts = analystsData ? analystsData.data : Array(12).fill(0);
          state.monthlyReports.data.customers = customersData
            ? customersData.data
            : Array(12).fill(0);
        }
      })
      .addCase(fetchReportsOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.data;
        state.nextToken = action.payload.nextToken;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchTopCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTopCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.topCustomers = action.payload;
      })
      .addCase(fetchTopCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchTotalReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTotalReports.fulfilled, (state, action) => {
        state.loading = false;
        state.totalReports = action.payload;
      })
      .addCase(fetchTotalReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchReportsLength.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReportsLength.fulfilled, (state, action) => {
        state.loading = false;
        state.reportsLength = action.payload;
      })
      .addCase(fetchReportsLength.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAnalystsReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAnalystsReports.fulfilled, (state, action) => {
        state.loading = false;
        state.analystsReports = action.payload;
      })
      .addCase(fetchAnalystsReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCustomersReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomersReports.fulfilled, (state, action) => {
        state.loading = false;
        state.customersReports = action.payload;
      })
      .addCase(fetchCustomersReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStatusReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStatusReports.fulfilled, (state, action) => {
        state.loading = false;
        state.statusReports = action.payload;
      })
      .addCase(fetchStatusReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        const report = action.payload;

        const processFindings = (findings) =>
          findings?.map((finding) => ({ ...finding, id: uuidv4() })) || [];

        if (report.company?.findings) {
          report.company.findings = processFindings(report.company.findings);
        }

        if (report.individual?.findings) {
          report.individual.findings = processFindings(report.individual.findings);
        }

        state.report = report;
        state.loading = false;
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateCompanyReport.pending, (state, { meta }) => {
        console.log('Pending updateCompanyReport:', meta.arg);
        state.loading = true;

        const { path, findingsId, value, operation } = meta.arg;
        const keys = path.split('.');

        if (path.startsWith('findings[') && findingsId) {
          const findingsKey = keys[0].split('[')[0];
          const findingsArray = state.report.company[findingsKey];

          const index = findingsArray.findIndex((item) => item.id === findingsId);
          if (index !== -1) {
            if (operation === 'delete') {
              console.log(`Deleting finding with id ${findingsId}`);
              findingsArray.splice(index, 1);
            } else {
              console.log(`Updating finding with id ${findingsId}`);
              findingsArray[index] = { ...findingsArray[index], ...value };
            }
          }
        } else {
          let target = state.report.company;
          for (let i = 0; i < keys.length - 1; i += 1) {
            target = target[keys[i]];
          }
          const lastKey = keys[keys.length - 1];
          target[lastKey] = value;
        }
      })
      .addCase(updateCompanyReport.fulfilled, (state, action) => {
        console.log('Fulfilled updateCompanyReport:', action.payload);
        state.loading = false;
      })
      .addCase(updateCompanyReport.rejected, (state, action) => {
        console.error('Rejected updateCompanyReport:', action.payload);
        state.loading = false;
        state.error = action.payload;

        if (action.meta.arg.previousReport) {
          console.log('Restoring previous report state:', action.meta.arg.previousReport);
          state.report = action.meta.arg.previousReport;
        } else {
          console.error('No previous report state available.');
        }
      })

      .addCase(updateScreeningReport.pending, (state, { meta }) => {
        state.loading = true;

        const { path, value } = meta.arg;
        if (state.report?.screening) {
          const keys = path.split('.');
          let target = state.report.screening;

          for (let i = 0; i < keys.length - 1; i += 1) {
            const key = keys[i];
            const match = key.match(/(\w+)\[(\d+)\]/);
            if (match) {
              target = target[match[1]][parseInt(match[2], 10)];
            } else {
              target = target[key];
            }
          }

          target[keys[keys.length - 1]] = value;
        }
      })
      .addCase(updateScreeningReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload;
      })
      .addCase(updateScreeningReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;

        if (action.meta.arg.previousReport) {
          state.report = action.meta.arg.previousReport;
        } else {
          console.error('Error');
        }
      });
  },
});

export default reportsSlice.reducer;
